import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React from 'react';
import styles from './download-loading-popup.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {DownloadStateTypes, updateDownloadLoadingPopupOpenState} from '@Components/download-panel/download-panel.reducer';
import {DownloadPanelDownloadingSection} from '@Components/download-panel/components/download-panel-downloading-section/download-panel-downloading-section';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {MODALS_IDS} from '@Components/modal-container';
import useWindowSize from '@Hooks/useWindowSize';
import {CircularProgress} from '@Components/circular-progress/circular-progress';
import {Text, TextSize} from '@Components/text';
import {useRestoreOngoingDownloads} from '@Hooks/download-dialog/useRestoreOngoingDownloads';
import {usePremiumDownloads} from '@Hooks/download-dialog/usePremiumDownloads';
import {usePaygDownloads} from '@Hooks/download-dialog/usePaygDownloads';
import {useDownloadProgressAnimation} from '@Hooks/download-dialog/useDownloadProgressAnimation';
import {useGenerateDownloadFromDownloadPage} from '@Hooks/download-dialog/useGenerateDownloadFromDownloadPage';

const MOBILE_SNACKBAR_SCREEN_THRESHOLD = 781;

export function DownloadLoadingPopup(): ReactElement {
  const orderStateHashmapNew = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmapNew;
  });
  const currentlyProcessingOrderIdKeys = useAppSelector((state) => {
    return state.downloadDialog.currentlyProcessingOrderIdKeys;
  });
  const showLoadingPopup = useAppSelector((state) => {
    return state.downloadDialog.showLoadingPopup;
  });
  const panelId = useAppSelector((state) => {
    return state.modals?.modalsHashmap?.[MODALS_IDS.DOWNLOAD_MODAL]?.panelProps.id as string | undefined;
  });
  useRestoreOngoingDownloads();
  usePremiumDownloads();
  usePaygDownloads();
  useGenerateDownloadFromDownloadPage();
  useDownloadProgressAnimation();

  const nonDropboxOrderIdKeys = currentlyProcessingOrderIdKeys.filter((key) => {
    const order = orderStateHashmapNew[key];
    return order && !order.isDropboxDownload;
  });

  const hasOrders = nonDropboxOrderIdKeys.length > 0;

  const currentOrderIdKey = nonDropboxOrderIdKeys[nonDropboxOrderIdKeys.length - 1];
  const currentOrder = currentOrderIdKey ? orderStateHashmapNew[currentOrderIdKey] : undefined;

  const isInDownloadState = currentOrder?.state === DownloadStateTypes.DOWNLOADING;
  const isDownloadInProgressOrReady = hasOrders;

  const dispatch = useAppDispatch();
  const {windowWidth} = useWindowSize();

  const showMobileVersion = (): boolean => {
    return windowWidth < MOBILE_SNACKBAR_SCREEN_THRESHOLD;
  };

  useEffect(() => {
    if (showMobileVersion() && currentOrder?.state === DownloadStateTypes.READY) {
      setTimeout(() => {
        dispatch(updateDownloadLoadingPopupOpenState(false));
      }, 5000);

      return;
    }

    if (currentOrder?.state === DownloadStateTypes.READY && !showLoadingPopup) {
      dispatch(updateDownloadLoadingPopupOpenState(true));
    }
  }, [currentOrder?.state]);

  const onClickCancel = (): void => {
    dispatch(updateDownloadLoadingPopupOpenState(false));
  };

  const showLoading = (): boolean => {
    if (!panelId) {
      return isDownloadInProgressOrReady && !!showLoadingPopup;
    }

    return isDownloadInProgressOrReady && !!showLoadingPopup;
  };

  const getLoader = (): ReactElement | null => {
    if (currentOrder?.displayProgress !== undefined) {
      return <CircularProgress value={currentOrder.displayProgress} />;
    }
    return null;
  };

  const getText = (): string => {
    if (!isDownloadInProgressOrReady) {
      return '';
    }
    if (isInDownloadState) {
      return window.i18next.t('pmwjs_downloading');
    }

    return window.i18next.t('pmwjs_downloaded');
  };

  if (showMobileVersion()) {
    return (
      <div id="download-loading-state" className={`${styles.mobileContainer} ${showLoading() ? styles.in : styles.out} flexbox flex-items-center flex-h-row radius-round`}>
        {getLoader()}
        <Text val={getText()} className={`content-body-white spacing-m-l-2`} size={TextSize.XXSMALL} />
        <Icon icon="icon-close" className="content-body-white spacing-m-l-4" size={IconSize.SIZE_ICON_16} onClick={onClickCancel} />
      </div>
    );
  }

  return (
    <div
      id="download-loading-state"
      className={`${styles.container} flex flex-h-row shadow-floating-elevation-3 gap-1 radius-8 border-s-standard ${showLoading() ? styles.in : ''}`}
    >
      <Icon
        onClick={onClickCancel}
        className={`_position-absolute ${styles.icon}`}
        icon="icon-close"
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.CIRCLE}
        type={IconType.SHADOW}
      />
      <div className="spacing-p-4 _full-width _overflow-auto">
        {isDownloadInProgressOrReady ? (
          <DownloadPanelDownloadingSection orderIdKey={currentOrderIdKey} isOpenedInLoadingPopup isOpenedInModal={false} isOpenedInEditor={false} isOpenedInPrintShop={false} />
        ) : null}
      </div>
    </div>
  );
}
